import React, { useState, useMemo } from "react";
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Switch,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { makeStyles, withStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CheckIcon from "@material-ui/icons/Check";
import XIcon from "@material-ui/icons/Clear";
import { upperFirst } from "lodash";
import { getPermissionTitle, getPermissionDescription } from "../../util/helpers";
import { useTranslation } from "react-i18next";

const CustomTooltip = withStyles({
  tooltip: {
    backgroundColor: "#93B5F0 !important",
    color: "#121212 !important",
    padding: "12px 16px !important",
    borderRadius: "4px !important",
    fontSize: "12px !important",
    fontFamily: "Roboto, sans-serif !important",
    boxShadow: "0px 2px 6px rgba(0,0,0,0.3) !important",
  },
  arrow: {
    color: "#93B5F0 !important",
  },
})(Tooltip);

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    minHeight: 450,
    width: "100%",
  },
  sidebar: {
    backgroundColor: "transparent",
    padding: 0,
    width: "100%",
  },
  sidebarItem: {
    padding: "12px 16px",
    cursor: "pointer",
    borderLeft: "3px solid transparent",
    transition: "all 0.2s ease-in-out",
    "& .MuiTypography-root": {
      fontWeight: 400,
      color: (props) => (props?.darkMode ? "#A0A0A0" : "#7A7A7A"),
    },
    "&:hover": {
      "& .MuiTypography-root": {
        color: (props) => (props?.darkMode ? "#FFFFFF" : "#121212"),
      },
    },
  },
  sidebarItemActive: {
    borderLeft: "3px solid #3F88FC",
    "& .MuiTypography-root": {
      fontWeight: "bold",
      color: (props) => (props?.darkMode ? "#FFFFFF" : "#121212"),
    },
  },
  moduleText: {
    fontSize: "14px",
    fontFamily: "Roboto, sans-serif",
  },
  contentArea: {
    padding: "0 0 0 32px",
    backgroundColor: "transparent",
    flex: 1,
  },
  permissionList: {
    backgroundColor: (props) => (props?.darkMode ? "#1E1E1E" : "transparent"),
    borderRadius: 8,
    boxShadow: "none",
    border: (props) => (props?.darkMode ? "1px solid #333333" : "1px solid #E2E8F0"),
  },
  permissionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 24px",
    transition: "opacity 0.2s ease-in-out",
  },
  permissionInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  permissionLabel: {
    fontFamily: "Roboto, sans-serif",
    fontSize: "15px",
    color: (props) => (props?.darkMode ? "#F5F5F5" : "#2D3748"),
  },
  actionContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  lineConnector: {
    flexGrow: 1,
    borderBottom: (props) => (props?.darkMode ? "1.5px dotted #555555" : "1.5px dotted #B0B0B0"),
    margin: "0 16px",
    alignSelf: "center",
  },
  infoIconActive: {
    color: "#3F88FC",
    fontSize: "18px",
    cursor: "pointer",
    transition: "color 0.2s ease-in-out",
    "&:hover": {
      color: "#1E6BDB",
    },
  },
  infoIconDisabled: {
    color: "#D1D5DB",
    fontSize: "18px",
    cursor: "not-allowed",
  },
  _detail: {
    color: (props) => (props?.darkMode ? "#F5F5F5" : "#121212"),
    font: "normal normal normal 18px/24px Roboto",
    display: "inline-flex",
    alignItems: "center",
  },
  // Custom styled Switch for Option 2 Disabled State
  customSwitch: {
    width: "36px !important",
    height: "20px !important",
    padding: "0 !important",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
    "& .MuiSwitch-switchBase": {
      padding: "1px !important",
      top: "0px !important",
      left: "0px !important",
      transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      "&.Mui-checked": {
        transform: "translateX(16px) !important",
        color: "#FFFFFF !important",
        "& + .MuiSwitch-track": {
          backgroundColor: "#3F88FC !important",
          opacity: "1 !important",
        },
      },
    },
    "& .MuiSwitch-thumb": {
      width: "16px !important",
      height: "16px !important",
      backgroundColor: "#FFFFFF",
      border: "0.32px solid #0000000A",
      boxShadow: "0px 1.94px 0.65px 0px #0000000F, 0px 1.94px 5.16px 0px #00000026",
    },
    "& .MuiSwitch-track": {
      borderRadius: "10px !important",
      backgroundColor: "#E5E5EA !important",
      opacity: "1 !important",

    },
    // Checked and disabled switches: blue knob on light-blue track
    "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled .MuiSwitch-thumb": {
      background: "#DFDFDF !important",
      opacity: "1 !important",

    },
    "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled": {
      "& + .MuiSwitch-track": {
        backgroundColor: "#6F9CEB !important",
        opacity: "1 !important",

      },
    },
    // Unchecked and disabled switches: grey knob on light-grey track
    "& .MuiSwitch-switchBase.Mui-disabled:not(.Mui-checked) .MuiSwitch-thumb": {
      backgroundColor: "#8C8C8C !important",
      opacity: "1 !important",

    },
    "& .MuiSwitch-switchBase.Mui-disabled:not(.Mui-checked)": {
      "& + .MuiSwitch-track": {
        backgroundColor: "#d9d9e0ff !important",
        opacity: "1 !important",
      },
    },
  },
}));

// Dependency resolution helper
export const resolveDependencies = (currentValues, permissionsList) => {
  let changed = true;
  const resolved = { ...currentValues };

  while (changed) {
    changed = false;
    permissionsList.forEach((perm) => {
      // If currently checked
      if (resolved[perm.id]) {
        let rules = perm.rules;
        if (typeof rules === "string") {
          try { rules = JSON.parse(rules); } catch (e) { }
        }

        if (rules && Array.isArray(rules.depends_on)) {
          const { depends_on, operator } = rules;
          const satisfies = depends_on.map((depName) => {
            const depPerm = permissionsList.find(
              (p) => p.module === perm.module && p.name === depName
            );
            return depPerm ? !!resolved[depPerm.id] : false;
          });

          let valid = false;
          if (operator === "OR") {
            valid = satisfies.some(Boolean);
          } else {
            // default is AND
            valid = satisfies.every(Boolean);
          }

          if (!valid) {
            resolved[perm.id] = false;
            changed = true;
          }
        }
      }
    });
  }
  return resolved;
};


const PermissionsManager = ({
  permissions = [],
  value = {},
  onChange,
  disabled = false,
  darkMode = false,
}) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles({ darkMode });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Helper to extract localized description text
  const getDescriptionText = (perm) => {
    return getPermissionDescription(perm, i18n);
  };

  // Get unique sorted modules from permissions list
  const modules = useMemo(() => {
    const uniqueModules = [...new Set(permissions.map((p) => p.module))];
    return uniqueModules.sort((a, b) => a.localeCompare(b));
  }, [permissions]);

  const [activeModule, setActiveModule] = useState(modules[0] || "");

  // Update active module if list changes
  React.useEffect(() => {
    if (modules.length && !modules.includes(activeModule)) {
      setActiveModule(modules[0]);
    }
  }, [modules, activeModule]);

  // Determine sorted permissions for the active module
  const activePermissions = useMemo(() => {
    const filtered = permissions.filter((p) => p.module === activeModule);
    return filtered.sort((a, b) => {
      const aOrder = Number(a.sort_order ?? 999);
      const bOrder = Number(b.sort_order ?? 999);
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.name.localeCompare(b.name);
    });
  }, [permissions, activeModule]);

  const [collapsedParents, setCollapsedParents] = useState({});

  const toggleParent = (parentName) => {
    setCollapsedParents((prev) => ({
      ...prev,
      [parentName]: !prev[parentName],
    }));
  };

  // Build a tree of root permissions and their children (same-module dependencies)
  const permissionTree = useMemo(() => {
    const items = activePermissions.map((perm) => {
      let rules = perm.rules;
      if (typeof rules === "string") {
        try {
          rules = JSON.parse(rules);
        } catch (e) {}
      }

      let parentName = null;
      if (rules && Array.isArray(rules.depends_on) && rules.depends_on.length > 0) {
        parentName = rules.depends_on.find((depName) =>
          activePermissions.some((p) => p.name === depName)
        );
      }

      return { ...perm, parentName };
    });

    const roots = items.filter((item) => !item.parentName);
    const childrenMap = {};
    items.forEach((item) => {
      if (item.parentName) {
        if (!childrenMap[item.parentName]) {
          childrenMap[item.parentName] = [];
        }
        childrenMap[item.parentName].push(item);
      }
    });

    return { roots, childrenMap };
  }, [activePermissions]);

  // Check if a specific permission is disabled due to hierarchy rules
  const isPermissionRuleDisabled = (perm) => {
    let rules = perm.rules;
    if (typeof rules === "string") {
      try { rules = JSON.parse(rules); } catch (e) { }
    }
    if (!rules || !Array.isArray(rules.depends_on)) return false;

    const { depends_on, operator } = rules;
    const satisfies = depends_on.map((depName) => {
      const depPerm = permissions.find(
        (p) => p.module === perm.module && p.name === depName
      );
      return depPerm ? !!value[depPerm.id] : false;
    });

    if (operator === "OR") {
      return !satisfies.some(Boolean);
    }
    return !satisfies.every(Boolean);
  };

  const handleToggle = (permId, checked) => {
    if (disabled) return;

    let updatedRaw = { ...value, [permId]: checked };

    const targetPerm = permissions.find((p) => p.id === permId);
    if (checked && targetPerm) {
      let targetRules = targetPerm.rules;
      if (typeof targetRules === "string") {
        try { targetRules = JSON.parse(targetRules); } catch (e) {}
      }
      const exclusiveNames = new Set(targetRules?.exclusive_with || []);

      permissions.forEach((p) => {
        if (p.module === targetPerm.module && p.id !== targetPerm.id) {
          let rules = p.rules;
          if (typeof rules === "string") {
            try { rules = JSON.parse(rules); } catch (e) {}
          }
          if (rules?.exclusive_with?.includes(targetPerm.name)) {
            exclusiveNames.add(p.name);
          }
        }
      });

      if (exclusiveNames.size > 0) {
        permissions.forEach((p) => {
          if (p.module === targetPerm.module && exclusiveNames.has(p.name)) {
            updatedRaw[p.id] = false;
          }
        });
      }
    }

    const updatedResolved = resolveDependencies(updatedRaw, permissions);
    onChange(updatedResolved);
  };

  return (
    <Box className={classes.container} style={isMobile ? { display: "block", minHeight: "auto" } : undefined}>
      {isMobile && modules.length > 0 && (
        <Box mb={2.5} width="100%">
          <Typography variant="subtitle2" style={{ marginBottom: 8, color: "#666666", fontWeight: 500, fontFamily: "Roboto, sans-serif" }}>
            {t("Select Module")}
          </Typography>
          <Select
            value={activeModule}
            onChange={(e) => setActiveModule(e.target.value)}
            variant="outlined"
            fullWidth
            style={{ backgroundColor: "#FFFFFF" }}
          >
            {modules.map((mod) => (
              <MenuItem key={mod} value={mod}>
                {t(mod)}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      <Grid container>
        {/* Left Module Sidebar Pane (Only on Desktop) */}
        {!isMobile && (
          <Grid item xs={12} md={3}>
            <List className={classes.sidebar}>
              {modules.map((mod) => (
                <ListItem
                  key={mod}
                  className={`${classes.sidebarItem} ${activeModule === mod ? classes.sidebarItemActive : ""
                    }`}
                  onClick={() => setActiveModule(mod)}
                >
                  <ListItemText
                    primary={t(mod)}
                    classes={{ primary: classes.moduleText }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        )}

        {/* Right Permissions Pane */}
        <Grid item xs={12} md={isMobile ? 12 : 9} className={classes.contentArea} style={isMobile ? { padding: 0 } : undefined}>
          <Card className={classes.permissionList}>
            <CardContent style={{ padding: 0 }}>
              {activePermissions.length === 0 ? (
                <Box p={4} textAlign="center">
                  <Typography color="textSecondary">{t("No permissions available")}</Typography>
                </Box>
              ) : (
                (() => {
                  const renderRow = (perm, isChild = false) => {
                    const ruleDisabled = isPermissionRuleDisabled(perm);
                    const isChecked = !!value[perm.id];
                    const isRowDisabled = disabled || ruleDisabled;
                    const children = permissionTree.childrenMap[perm.name] || [];
                    const hasChildren = children.length > 0;
                    const isCollapsed = !!collapsedParents[perm.name];

                    return (
                      <React.Fragment key={perm.id}>
                        <Box
                          className={classes.permissionRow}
                          style={{
                            paddingLeft: isChild ? 56 : 24,
                            opacity: !disabled && ruleDisabled ? 0.6 : 1,
                            cursor: hasChildren ? "pointer" : "default",
                          }}
                          onClick={hasChildren ? () => toggleParent(perm.name) : undefined}
                        >
                          <Typography className={classes.permissionLabel}>
                            {upperFirst(getPermissionTitle(perm, i18n))}
                            {hasChildren && (
                              <span style={{ marginLeft: 8, fontSize: "12px", color: "#718096", display: "inline-flex", alignItems: "center" }}>
                                {isCollapsed ? "▸" : "▾"}
                              </span>
                            )}
                          </Typography>

                          <div className={classes.lineConnector} />

                          <Box className={classes.actionContainer} onClick={(e) => e.stopPropagation()}>
                            {disabled ? (
                              <Typography className={classes._detail} variant="h6">
                                {isChecked ? <CheckIcon /> : <XIcon />}
                              </Typography>
                            ) : (
                              <Switch
                                checked={isChecked}
                                onChange={(e) => handleToggle(perm.id, e.target.checked)}
                                disabled={isRowDisabled}
                                color="primary"
                                className={classes.customSwitch}
                              />
                            )}

                            {/* Tooltip info button */}
                            {(() => {
                              const descriptionText = getDescriptionText(perm);
                              return descriptionText ? (
                                <CustomTooltip title={descriptionText} arrow placement="top">
                                  <InfoOutlinedIcon className={classes.infoIconActive} />
                                </CustomTooltip>
                              ) : (
                                <InfoOutlinedIcon className={classes.infoIconDisabled} />
                              );
                            })()}
                          </Box>
                        </Box>
                        {hasChildren && !isCollapsed && children.map((child) => renderRow(child, true))}
                      </React.Fragment>
                    );
                  };

                  return permissionTree.roots.map((root) => renderRow(root, false));
                })()
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PermissionsManager;
